import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, Fragment, memo } from 'react'
import ReactDOM from 'react-dom'
import debounce from 'lodash-es/debounce'

import styled, { useTheme } from 'styled-components'
import { Node, Transforms, Path, Editor } from 'vendor/slate'
import { useReadOnly, ReactEditor } from 'vendor/slate-react'
import { useDrag, useDrop } from 'react-dnd'
import {
  ELEMENT_TYPES,
  ELEMENT_TYPES_ARRAY,
  ELEMENT_TYPES_BY_KEY,
  NEW_ELEMENT_MENU_ITEMS,
  EXISTING_ELEMENT_MENU_ITEMS,
  NEW_CHILD_ELEMENT_MENU_ITEMS
} from 'cg/components/documents/editor/slate/types'
import Select, { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import { useMutation, useApolloClient } from '@apollo/client'
import { Loader } from 'cg/components/shared/loader'
import { deepGet } from 'cg/helpers'
import { Button } from 'react-bootstrap'
import { getSelectTheme } from 'Shared/theme'
import addElement from 'cg/components/documents/editor/slate/addElement'
import { UploadFileMutation, SearchLink } from 'cg/components/documents/editor/slate/operations.graphql'
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
import { v4 as uuidv4 } from 'uuid'

const OPENING_ES_TAG = '<eshighlight>'
const CLOSING_ES_TAG = '</eshighlight>'
const REPLACEMENT_OPENING_ES_TAG = '<mark>'
const REPLACEMENT_CLOSING_ES_TAG = '</mark>'

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const generateDisplayLink = source => {
  if (source.project_title) {
    return `${source.project_title}/${source.title}`
  }
  if (source.repository_title) {
    return `${source.repository_title}/${source.title}`
  }
  if (source.document_title) {
    const document_title = source.document_section_group_path
      ? `${source.document_section_group_path}/${source.title}`
      : source.title
    if (source.document_version_title) {
      return `${source.document_title}/${source.document_version_title}/${document_title}`
    }

    return `${source.document_title}/${document_title}`
  }
  return `${source.title}`
}

const SidebarElementMenu = props => {
  const {
    onFileUploadInputChange,
    fileUploadRef,
    onImageUploadInputChange,
    imageUploadRef,
    showDelete,
    showCopyToClipboard,
    deleteElement,
    copyToClipboard,
    insertElementBelow,
    insertElementAbove,
    showLineBreaks,
    selectFixedOptionsRef,
    onSearchChange,
    loadSearchOptions,
    onChange,
    selectRef,
    linkSearchType,
    childSidebarElementMenu,
    options,
    value,
    uploadFileLoading,
    setIsOpen,
    hasElement,
    isOpen,
    sidebarToggleRef,
    sidebarPosition,
    sidebarPopoverRef,
    setLinkSearchType,
    setIsCopied
  } = props
  const sidebarStyle = { ...sidebarPosition, opacity: sidebarPosition ? '1' : '0' }

  const theme = useTheme()

  return (
    <>
      {uploadFileLoading && <Loader />}
      <SidebarButton>
        <SidebarToggleButton
          size="sm"
          variant={isOpen ? 'primary' : 'secondary'}
          $childSidebarElementMenu={childSidebarElementMenu}
          innerRef={sidebarToggleRef}
          $isOpen={isOpen}
          $hasElement={hasElement()}
          onMouseDown={e => {
            e.preventDefault()
            if (!uploadFileLoading) setIsOpen(!isOpen)
          }}
        >
          {!hasElement() ? <i className="far fa-plus" /> : <i className="far fa-chevron-down" />}
        </SidebarToggleButton>
      </SidebarButton>
      {isOpen &&
        sidebarToggleRef &&
        ReactDOM.createPortal(
          <SidebarPopover style={sidebarStyle} ref={sidebarPopoverRef}>
            <SidebarCover
              onMouseDown={e => {
                e.preventDefault()
                if (!uploadFileLoading) {
                  setIsOpen(false)
                }
                setLinkSearchType(null)
              }}
            />
            <SidebarMenu>
              {!linkSearchType && options.length > 0 && (
                <Select
                  ref={selectRef}
                  styles={SelectStyles({ theme })}
                  theme={props => ({
                    ...props,
                    colors: {
                      ...props.colors,
                      ...getSelectTheme(theme.id)
                    },
                    borderRadius: 3,
                    spacing: {
                      controlHeight: 34,
                      baseUnit: 3,
                      menuGutter: 6
                    }
                  })}
                  value={value}
                  autoFocus
                  controlShouldRenderValue={false}
                  hideSelectedOptions={false}
                  isClearable={false}
                  isMulti={false}
                  backspaceRemovesValue={false}
                  tabSelectsValue={false}
                  menuIsOpen
                  components={{ Option, DropdownIndicator, IndicatorSeparator: null }}
                  options={options}
                  placeholder="Filter..."
                  getOptionLabel={option => option.label}
                  getOptionValue={option => option.type}
                  onChange={data => onChange(data.type)}
                />
              )}
              {linkSearchType && (
                <AsyncSelect
                  cacheOptions={false}
                  loadOptions={loadSearchOptions}
                  isMulti={false}
                  autoFocus
                  ref={selectRef}
                  styles={SelectStyles({ theme })}
                  menuIsOpen
                  onChange={onSearchChange}
                  components={{ Option: SearchOption, DropdownIndicator, IndicatorSeparator: null }}
                  theme={props => ({
                    ...props,
                    colors: {
                      ...props.colors,
                      ...getSelectTheme(theme.id)
                    },
                    borderRadius: 3,
                    spacing: {
                      controlHeight: 34,
                      baseUnit: 3,
                      menuGutter: 6
                    }
                  })}
                  placeholder={`Search ${ELEMENT_TYPES_BY_KEY[linkSearchType].label}s...`}
                />
              )}
              {hasElement() && (
                <SidebarMenuFixedOptions ref={selectFixedOptionsRef} $hasOptions={options.length > 0}>
                  {showLineBreaks && (
                    <SidebarMenuFixedOption
                      $themeId={theme.id}
                      onMouseDown={event => {
                        event.preventDefault()
                        insertElementAbove()
                      }}
                    >
                      <SidebarMenuIcon>
                        <i className="far fa-arrow-to-top" />
                      </SidebarMenuIcon>
                      Add Block Above
                    </SidebarMenuFixedOption>
                  )}
                  {showLineBreaks && (
                    <SidebarMenuFixedOption
                      $themeId={theme.id}
                      onMouseDown={event => {
                        event.preventDefault()
                        insertElementBelow()
                      }}
                    >
                      <SidebarMenuIcon>
                        <i className="far fa-arrow-to-bottom" />
                      </SidebarMenuIcon>
                      Add Block Below
                    </SidebarMenuFixedOption>
                  )}
                  {showCopyToClipboard && (
                    <SidebarMenuFixedOption
                      $themeId={theme.id}
                      onMouseDown={event => {
                        event.preventDefault()
                        copyToClipboard()
                        setIsCopied(true)
                        setTimeout(() => {
                          setIsCopied(false)
                        }, 1000)
                      }}
                    >
                      <SidebarMenuIcon>
                        <i className="far fa-clipboard" />
                      </SidebarMenuIcon>
                      Copy to Clipboard
                    </SidebarMenuFixedOption>
                  )}
                  {showDelete && (
                    <SidebarMenuFixedOption
                      $themeId={theme.id}
                      onMouseDown={event => {
                        event.preventDefault()
                        deleteElement()
                      }}
                    >
                      <SidebarMenuIcon>
                        <i className="far fa-trash-alt" />
                      </SidebarMenuIcon>
                      Delete
                    </SidebarMenuFixedOption>
                  )}
                </SidebarMenuFixedOptions>
              )}
              <input
                type="file"
                accept="image/*"
                ref={imageUploadRef}
                onChange={onImageUploadInputChange}
                style={{ display: 'none' }}
              />
              <input type="file" ref={fileUploadRef} onChange={onFileUploadInputChange} style={{ display: 'none' }} />
            </SidebarMenu>
          </SidebarPopover>,
          document.body
        )}
    </>
  )
}

const Sidebar = memo(
  ({
    editor,
    element,
    children,
    attributes,
    workspaceId,
    handleOpenComments = null,
    commentReferences,
    commentCounters,
    handleSetCommentsOffset,
    deleteMention
  }) => {
    const client = useApolloClient()
    const readOnly = useReadOnly()
    const { type, parent } = element
    const parentNode = ReactEditor.findParent(editor, element)
    const theme = useTheme()

    const [isOpen, setIsOpen] = useState(false)
    const [isCopied, setIsCopied] = useState(false)
    const [linkSearchType, setLinkSearchType] = useState(null)

    const [sidebarPosition, setSidebarPosition] = useState(null)

    const sidebarToggleRef = useRef(null)
    const sidebarPopoverRef = useRef(null)
    const selectRef = useRef(null)
    const selectFixedOptionsRef = useRef(null)

    const imageUploadRef = useRef(null)
    const [uploadFileLoading, setUploadFileLoading] = useState(null)

    const fileUploadRef = useRef(null)

    const prevIsOpen = usePrevious(isOpen)

    const uploadFileMutation = useMutation(UploadFileMutation)
    const [uploadFile] = uploadFileMutation

    const isCommentsOpen = commentReferences && commentReferences.indexOf(element.uuid) !== -1

    const calculateSidebarMenuPosition = useCallback(() => {
      const sidebarBoundingClientRect = sidebarToggleRef ? sidebarToggleRef.current.getBoundingClientRect() : null

      const selectControlRefHeight =
        selectRef && selectRef.current && selectRef.current.select && selectRef.current.select.controlRef
          ? selectRef.current.select.controlRef.getBoundingClientRect().height
          : 0
      const selectMenuListRefHeight =
        selectRef && selectRef.current && selectRef.current.select && selectRef.current.select.menuListRef
          ? selectRef.current.select.menuListRef.getBoundingClientRect().height
          : 0
      const selectFixedOptionsHeight =
        selectFixedOptionsRef && selectFixedOptionsRef.current && selectFixedOptionsRef.current
          ? selectFixedOptionsRef.current.getBoundingClientRect().height
          : 0

      const selectRefHeight = selectControlRefHeight + selectMenuListRefHeight + selectFixedOptionsHeight

      const top = sidebarBoundingClientRect ? sidebarBoundingClientRect.y : 0
      const left = sidebarBoundingClientRect ? sidebarBoundingClientRect.x + sidebarBoundingClientRect.width : 0

      const style = {
        top: `${top}px`,
        left: `${left}px`
      }

      if (
        sidebarBoundingClientRect &&
        sidebarBoundingClientRect.bottom + selectRefHeight >= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        style.top = undefined
        style.bottom = `${document.documentElement.clientHeight - top - selectControlRefHeight}px`
      }
      return style
    }, [])

    const resizeListener = useCallback(() => {
      if (sidebarPopoverRef && sidebarPopoverRef.current) {
        setSidebarPosition(calculateSidebarMenuPosition())
      }
    }, [calculateSidebarMenuPosition, sidebarPopoverRef])

    useEffect(() => {
      if (!prevIsOpen && isOpen) {
        setSidebarPosition(calculateSidebarMenuPosition)
        window.removeEventListener('resize', debounce(resizeListener, 300))
        window.addEventListener('resize', debounce(resizeListener, 300))
      }
      if (prevIsOpen && !isOpen) {
        setSidebarPosition(null)
        window.removeEventListener('resize', debounce(resizeListener, 300))
      }

      return window.removeEventListener('resize', resizeListener)
    }, [calculateSidebarMenuPosition, isOpen, prevIsOpen, resizeListener, sidebarPosition])

    const hasElement = () => {
      if (!element || !type) return false
      if (type === ELEMENT_TYPES.default) {
        let text = { text: '' }

        try {
          const [[texts]] = Node.texts(element)
          text = texts
        } catch (err) {}

        if (text && text.text && text.text.length) {
          return true
        }

        if ('children' in element) {
          return element.children.some(n => 'type' in n && ELEMENT_TYPES_ARRAY.includes(n.type))
        }

        return false
      }

      return true
    }

    const onChange = type => {
      switch (type) {
        case ELEMENT_TYPES.file:
          fileUploadRef.current.click()
          break
        case ELEMENT_TYPES.image:
          imageUploadRef.current.click()
          break
        case ELEMENT_TYPES.documentlink:
        case ELEMENT_TYPES.issuelink:
        case ELEMENT_TYPES.commitlink:
        case ELEMENT_TYPES.mrlink:
          setLinkSearchType(type)
          break
        default:
          addElement(editor, element, type, ReactEditor.findPath(editor, element))
          setIsOpen(false)
      }
    }

    const onSearchChange = value => {
      const { source } = value
      const { link_without_namespace_and_workspace, absolute_link, publish_link, publish_display_link, publish_absolute_link } =
        source
      const title = generateDisplayLink(source)

      addElement(editor, element, linkSearchType, ReactEditor.findPath(editor, element), {
        title,
        link_without_namespace_and_workspace: link_without_namespace_and_workspace || null,
        absolute_link: absolute_link || null,
        publish_link: publish_link || null,
        publish_display_link: publish_display_link || null,
        publish_absolute_link: publish_absolute_link || null
      })

      setLinkSearchType(null)
      setIsOpen(false)
    }

    const onImageUploadInputChange = () => {
      const file = imageUploadRef.current.files[0]

      if (!file) return

      setUploadFileLoading(true)
      uploadFile({
        variables: { workspaceId, file }
      })
        .then(data => {
          const url = deepGet(data, 'data.uploadFile.file')
          addElement(editor, element, ELEMENT_TYPES.image, ReactEditor.findPath(editor, element), { url })
          setUploadFileLoading(null)
          setIsOpen(false)
        })
        .catch(err => {
          setUploadFileLoading(null)
          setIsOpen(false)
        })
    }

    const onFileUploadInputChange = () => {
      const file = fileUploadRef.current.files[0]

      if (!file) return

      setUploadFileLoading(true)
      uploadFile({
        variables: { workspaceId, file }
      })
        .then(data => {
          const url = deepGet(data, 'data.uploadFile.file')
          const title = deepGet(data, 'data.uploadFile.title')
          const size = deepGet(data, 'data.uploadFile.size')

          addElement(editor, element, ELEMENT_TYPES.file, ReactEditor.findPath(editor, element), {
            url,
            title,
            size
          })
          setUploadFileLoading(null)
          setIsOpen(false)
        })
        .catch(err => {
          setUploadFileLoading(null)
          setIsOpen(false)
        })
    }

    const deleteElement = () => {
      const path = ReactEditor.findPath(editor, element)
      const nextPathExists = Node.has(editor, Path.next(path))
      const noNewLineAfterDelete = !!element.no_new_line_after_delete

      for (const [match] of Editor.nodes(editor, {
        at: path,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && ELEMENT_TYPES.mention === n.type
      })) {
        deleteMention(match)
      }

      Transforms.removeNodes(editor, { at: path })

      if (!noNewLineAfterDelete) {
        if (!nextPathExists) {
          Transforms.insertNodes(
            editor,
            { type: ELEMENT_TYPES.default, children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }] },
            { at: path, select: true }
          )
          ReactEditor.focus(editor)
        } else {
          const point = Editor.end(editor, path)
          Transforms.select(editor, point)
          ReactEditor.focus(editor)
        }
      }

      setIsOpen(false)
    }
    const copyToClipboard = () => {
      const string = JSON.stringify(element)
      const encoded = window.btoa(encodeURIComponent(string))
      const mimeType = 'application/x-slate-clipboard'
      const newData = `${mimeType}: ${encoded}`

      if (window.clipboard && window.clipboard.writeText) {
        // Modern approach with Clipboard API
        window.clipboard
          .writeText(newData)
          .then(() => {
            setIsOpen(false)
          })
          .catch(err => {})
      } else {
        // Fallback for older browsers using execCommand
        const textArea = document.createElement('textarea')
        textArea.value = newData
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
          setIsOpen(false)
        } catch (err) {
          // void
        } finally {
          document.body.removeChild(textArea)
        }
      }
    }

    const insertElementAbove = () => {
      const path = ReactEditor.findPath(editor, element)
      setIsOpen(false)
      Transforms.insertNodes(
        editor,
        { type: ELEMENT_TYPES.default, children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }] },
        { at: path, select: true }
      )
      ReactEditor.focus(editor)
    }

    const insertElementBelow = () => {
      const path = ReactEditor.findPath(editor, element)
      setIsOpen(false)
      Transforms.insertNodes(
        editor,
        { type: ELEMENT_TYPES.default, children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }] },
        { at: Path.next(path), select: true }
      )
      ReactEditor.focus(editor)
    }

    const [{ isOver, dragDirection }, drop] = useDrop({
      accept: 'SidebarBlock',
      collect(monitor) {
        const item = monitor.getItem()
        if (!item || item.type !== 'SidebarBlock') return { isOver: false, dragDirection: null }

        const itemPath = item ? ReactEditor.findPath(editor, item.element) : null
        const elementPath = item ? ReactEditor.findPath(editor, element) : null
        const isOver = item
          ? monitor.isOver({ shallow: true }) && JSON.stringify(itemPath) !== JSON.stringify(elementPath)
          : false
        const initialOffset = monitor.getInitialClientOffset()
        const clientOffset = monitor.getClientOffset()
        const dragDirection = initialOffset && clientOffset ? (initialOffset.y < clientOffset.y ? 'down' : 'up') : null
        return { isOver, dragDirection: isOver && dragDirection ? dragDirection : null }
      },
      drop(item) {
        const atPath = ReactEditor.findPath(editor, item.element)
        const toPath = ReactEditor.findPath(editor, element)

        // Don't replace items with themselves
        if (!atPath || !toPath || JSON.stringify(atPath) === JSON.stringify(toPath) || atPath.length !== toPath.length) {
          return
        }

        Transforms.moveNodes(editor, {
          at: atPath,
          to: toPath
        })
      }
    })

    const abortController = React.useRef()
    const debouncedQuery = React.useRef(
      debounce((query, args, callback) => {
        const controller = new window.AbortController()
        abortController.current = controller

        query({
          query: args.query,
          variables: args.variables,
          options: {
            context: { fetchOptions: { signal: controller.signal } }
          }
        }).then(
          result => {
            callback(result.data.linkSearch.hits?.hits)
          },
          error => {
            callback([])
          }
        )
      }, 500)
    )

    const abortLatest = () => abortController.current && abortController.current.abort()

    const [, drag, preview] = useDrag({
      type: 'SidebarBlock',
      item: { element }
    })

    const loadSearchOptions = (query, callback) => {
      abortLatest()
      if (!query.length) callback([])
      debouncedQuery.current(
        client.query,
        {
          query: SearchLink,
          variables: {
            query,
            workspaceId,
            filter: ELEMENT_TYPES_BY_KEY[linkSearchType].filter,
            from: 0,
            limit: 50,
            searchFields: ELEMENT_TYPES_BY_KEY[linkSearchType].fields
          }
        },
        callback
      )
    }

    const showchildSidebarElementMenu = parent && parent.type ? !!NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type] : false
    const showLineBreaks = !(
      parent &&
      parent.type &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type] &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].no_line_breaks
    )
    const noMargin = !!(element && element.noMargin)

    const showDelete = !(
      parent &&
      parent.type &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type] &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].min_children &&
      parentNode &&
      parentNode.children.length <= NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].min_children
    )

    const showCopyToClipboard = !(
      parent &&
      parent.type &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type] &&
      NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].min_children &&
      parentNode &&
      parentNode.children.length <= NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].min_children
    )
    const commentButtonRef = useRef()

    const handleToggleComment = () => {
      if (!handleOpenComments) return

      if (isCommentsOpen) {
        handleSetCommentsOffset(null)
        handleOpenComments(null)
      } else {
        let { uuid } = element

        if (!uuid) {
          uuid = uuidv4()
          Transforms.setNodes(editor, { uuid }, { at: ReactEditor.findPath(editor, element) })
        }

        if (commentButtonRef.current) {
          handleSetCommentsOffset(commentButtonRef.current.offsetParent ? commentButtonRef.current.offsetParent.offsetTop : 0)
        }

        handleOpenComments([uuid])
      }
    }

    useLayoutEffect(() => {
      if (isCommentsOpen) {
        if (commentButtonRef.current) {
          handleSetCommentsOffset(commentButtonRef.current.offsetParent ? commentButtonRef.current.offsetParent.offsetTop : 0)
        }
      }
    }, [handleSetCommentsOffset, isCommentsOpen])

    const commentCount = useMemo(() => {
      if (!commentCounters || !element.uuid) return null
      let counter = null
      commentCounters.forEach(counters => {
        if (counters.references && counters.references[0] === element.uuid) {
          counter = counters.count
        }
      })
      return counter
    }, [commentCounters, element.uuid])

    return (
      <ElementMargin>
        {(!parent || !parent.type) && !readOnly ? (
          <div ref={drop}>
            <ElementWrapper
              ref={preview}
              $readOnly={readOnly}
              $childSidebarElementMenu={showchildSidebarElementMenu}
              $noMargin={noMargin}
            >
              <SidebarWrapper
                $isOpen={isOpen}
                contentEditable={false}
                style={{ userSelect: 'none' }}
                $childSidebarElementMenu={showchildSidebarElementMenu}
              >
                <DragHandle ref={drag}>
                  <i className="fa fa-grip-vertical" />
                </DragHandle>
                <SidebarElementMenu
                  setIsCopied={setIsCopied}
                  onFileUploadInputChange={onFileUploadInputChange}
                  fileUploadRef={fileUploadRef}
                  onImageUploadInputChange={onImageUploadInputChange}
                  imageUploadRef={imageUploadRef}
                  showDelete={showDelete}
                  showCopyToClipboard={showCopyToClipboard}
                  deleteElement={deleteElement}
                  copyToClipboard={copyToClipboard}
                  insertElementBelow={insertElementBelow}
                  insertElementAbove={insertElementAbove}
                  showLineBreaks={showLineBreaks}
                  selectFixedOptionsRef={selectFixedOptionsRef}
                  onSearchChange={onSearchChange}
                  loadSearchOptions={loadSearchOptions}
                  onChange={onChange}
                  selectRef={selectRef}
                  linkSearchType={linkSearchType}
                  setLinkSearchType={setLinkSearchType}
                  sidebarPopoverRef={sidebarPopoverRef}
                  setIsOpen={setIsOpen}
                  hasElement={hasElement}
                  sidebarToggleRef={sidebarToggleRef}
                  isOpen={isOpen}
                  uploadFileLoading={uploadFileLoading}
                  sidebarPosition={sidebarPosition}
                  options={
                    hasElement() && EXISTING_ELEMENT_MENU_ITEMS[type]
                      ? EXISTING_ELEMENT_MENU_ITEMS[type].items || []
                      : NEW_ELEMENT_MENU_ITEMS || []
                  }
                  value={ELEMENT_TYPES_BY_KEY[type] || {}}
                  $childSidebarElementMenu={showchildSidebarElementMenu}
                />
              </SidebarWrapper>
              <ElementItem
                $isCopied={isCopied}
                $themeId={theme.id}
                className="element"
                $isOver={isOver}
                $dragDirection={dragDirection}
                $childSidebarElementMenu={showchildSidebarElementMenu}
              >
                {children}
              </ElementItem>
              {handleOpenComments && !readOnly && (
                <CommentButton
                  innerRef={commentButtonRef}
                  variant={isCommentsOpen ? 'secondary' : 'outline-secondary'}
                  contentEditable={false}
                  style={{ userSelect: 'none' }}
                  onClick={handleToggleComment}
                  $isOpen={isCommentsOpen || commentCount}
                >
                  <i className="fal fa-comment" />
                  {commentCount && <div className="ms-1">{commentCount}</div>}
                </CommentButton>
              )}
            </ElementWrapper>
          </div>
        ) : (
          <ElementMargin>
            {!readOnly && showchildSidebarElementMenu ? (
              <ElementWrapper $childSidebarElementMenu={showchildSidebarElementMenu} $readOnly={readOnly} $noMargin={noMargin}>
                <SidebarWrapper
                  $isOpen={isOpen}
                  contentEditable={false}
                  style={{ userSelect: 'none' }}
                  $childSidebarElementMenu={showchildSidebarElementMenu}
                >
                  <SidebarElementMenu
                    setIsCopied={setIsCopied}
                    onFileUploadInputChange={onFileUploadInputChange}
                    fileUploadRef={fileUploadRef}
                    onImageUploadInputChange={onImageUploadInputChange}
                    imageUploadRef={imageUploadRef}
                    showDelete={showDelete}
                    showCopyToClipboard={showCopyToClipboard}
                    deleteElement={deleteElement}
                    copyToClipboard={copyToClipboard}
                    insertElementBelow={insertElementBelow}
                    insertElementAbove={insertElementAbove}
                    showLineBreaks={showLineBreaks}
                    selectFixedOptionsRef={selectFixedOptionsRef}
                    onSearchChange={onSearchChange}
                    loadSearchOptions={loadSearchOptions}
                    onChange={onChange}
                    selectRef={selectRef}
                    linkSearchType={linkSearchType}
                    setLinkSearchType={setLinkSearchType}
                    sidebarPopoverRef={sidebarPopoverRef}
                    setIsOpen={setIsOpen}
                    hasElement={hasElement}
                    sidebarToggleRef={sidebarToggleRef}
                    isOpen={isOpen}
                    uploadFileLoading={uploadFileLoading}
                    sidebarPosition={sidebarPosition}
                    options={
                      hasElement() && EXISTING_ELEMENT_MENU_ITEMS[type]
                        ? EXISTING_ELEMENT_MENU_ITEMS[type].items || []
                        : NEW_CHILD_ELEMENT_MENU_ITEMS[parent.type].items || []
                    }
                    value={ELEMENT_TYPES_BY_KEY[type] || {}}
                    $childSidebarElementMenu={showchildSidebarElementMenu}
                  />
                </SidebarWrapper>
                <ElementItem
                  $isCopied={isCopied}
                  $themeId={theme.id}
                  className="element"
                  $childSidebarElementMenu={showchildSidebarElementMenu}
                >
                  {children}
                </ElementItem>
              </ElementWrapper>
            ) : (
              <ElementWrapper $childSidebarElementMenu={showchildSidebarElementMenu} $readOnly={readOnly} $noMargin={noMargin}>
                <ElementItem
                  $isCopied={isCopied}
                  $themeId={theme.id}
                  className="element"
                  $childSidebarElementMenu={showchildSidebarElementMenu}
                >
                  {children}
                </ElementItem>
              </ElementWrapper>
            )}
          </ElementMargin>
        )}
      </ElementMargin>
    )
  }
)

export default Sidebar

const CustomCommentButton = ({ $isOpen, innerRef, children, ...props }) => (
  <Button {...props} ref={innerRef}>
    {children}
  </Button>
)

const CommentButton = styled(CustomCommentButton)`
  position: absolute;
  left: 100%;
  top: 0;
  display: flex;
  align-items: center;
  color: ${props => props.theme.textGreyTinted};
  opacity: 0;
  outline: none;
  -webkit-transition: opacity 150ms ease-out;
  -moz-transition: opacity 150ms ease-out;
  -o-transition: opacity 150ms ease-out;
  transition: opacity 150ms ease-out;
  white-space: nowrap;
  i {
    font-size: 20px;
  }
  ${props =>
    props.$isOpen &&
    `
    opacity: 1;
  `}
`

const ElementMargin = styled.div`
  margin: 0px;
  display: block;
  padding: 0px;
  position: relative;
`

const DragHandle = styled.div`
  display: flex;
  color: ${props => props.theme.textGreyTinted};
  margin-right: 12px;
  cursor: grab;
  i {
    font-size: 13px;
  }
`

const SidebarWrapper = styled.div`
  align-items: center;
  display: flex;
  padding: ${props => (props.$childSidebarElementMenu ? '0px' : '0px 10px')};
  position: ${props => (props.$childSidebarElementMenu ? 'relative' : 'static')};
  min-width: ${props => (props.$childSidebarElementMenu ? '30px' : '72px')};
  width: ${props => (props.$childSidebarElementMenu ? '30px' : '72px')};
  left: ${props => (props.$childSidebarElementMenu ? '-30px' : 'auto')};
  margin-right: ${props => (props.$childSidebarElementMenu ? '-30px' : 'auto')};
  margin-bottom: 15px;
  -webkit-transition: opacity 150ms ease-out;
  -moz-transition: opacity 150ms ease-out;
  -o-transition: opacity 150ms ease-out;
  transition: opacity 150ms ease-out;
  opacity: ${props => (props.$isOpen ? '1' : '0')};
`

const ElementItem = styled.div`
  display: block;
  border-top: ${props => (props.$childSidebarElementMenu ? '0px' : '3px solid transparent')};
  border-bottom: ${props => (props.$childSidebarElementMenu ? '0px' : '3px solid transparent')};
  -webkit-transition: border 450ms ease-out;
  -moz-transition: border 450ms ease-out;
  -o-transition: border 450ms ease-out;
  transition: border 450ms ease-out;
  border-top-color: ${props => (props.$isOver && props.$dragDirection === 'up' ? 'rgba(154, 105, 252, 0.53)' : 'transparent')};
  border-bottom-color: ${props =>
    props.$isOver && props.$dragDirection === 'down' ? 'rgba(154, 105, 252, 0.53)' : 'transparent'};
  border-radius: ${props => (props.$isCopied ? '4px' : '0px')};
  padding: ${props => (!props.$isCopied ? `none` : `4px 8px`)};
  box-shadow: ${props => (!props.$isCopied ? 'none' : `0 0 0 1px ${getSelectTheme(props.$themeId).primary}`)};
`

const ElementWrapper = styled.div`
  display: flex;
  align-items: center;
  &:hover > ${SidebarWrapper} {
    opacity: 1;
  }
  &:hover > ${CommentButton} {
    opacity: 1;
  }
  > ${ElementItem} {
    width: ${props => (props.$readOnly || props.$childSidebarElementMenu || props.$noMargin ? '100%' : 'calc(100% - 72px)')};
  }
`

const CustomButton = ({ childSidebarElementMenu, $isOpen, hasElement, innerRef, children, ...props }) => (
  <Button {...props} ref={innerRef}>
    {children}
  </Button>
)

const SidebarToggleButton = styled(CustomButton)`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => (props.$childSidebarElementMenu ? '20px' : '25px')};
  height: ${props => (props.$childSidebarElementMenu ? '20px' : '25px')};
  color: ${props => (props.$isOpen ? props.theme.white : props.theme.textGreyTinted)};
  transform: ${props => (props.$isOpen ? (!props.$hasElement ? 'rotate(45deg)' : 'rotate(180deg)') : 'none')};
  i {
    font-size: ${props => (props.$childSidebarElementMenu ? '12px' : '13px')};
  }
`

const SidebarButton = styled.div`
  display: flex;
`
const SidebarPopover = styled.div`
  position: absolute;
  padding-top: 5px;
  padding-bottom: 5px;
`

const SidebarCover = styled.div`
  bottom: 0;
  left: 0;
  top: 0;
  right: 0;
  position: fixed;
  z-index: 100;
`

const SidebarMenu = styled.div`
  position: relative;
  z-index: 101;
  border: 1px solid ${props => props.theme.borderColor};
  border: 1px solid ${props => props.theme.borderColor};
  overflow: hidden;
  background: ${props => props.theme.bgLightest};
  border-radius: 4px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
`

const SidebarMenuFixedOptions = styled.div`
  min-width: 220px;
  box-shadow: ${props => (props.$hasOptions ? '0px -1px 5px rgba(0, 0, 0, 0.07)' : 'none')};
  border-top: ${props => (props.$hasOptions ? '1px solid rgba(0, 0, 0, 0.1)' : '0px')};
`

const SidebarMenuFixedOption = styled.div`
  padding: 6px 9px;
  width: 100%;
  font-size: 13.75px;
  user-select: none;
  color: inherit;
  &:hover {
    background: ${props => (props.$themeId ? getSelectTheme(props.$themeId).primary25 : null)};
  }
  &:active {
    background: ${props => (props.$themeId ? getSelectTheme(props.$themeId).primary50 : null)};
  }
`

const SidebarMenuIcon = styled.span`
  margin-right: 10px;
  width: 15px;
  display: inline-flex;
  justify-content: center;
`

const ResultLink = styled.div`
  font-size: 0.945rem;
  margin-bottom: 10px;
  color: ${props => props.theme.linkColor};
  &:hover {
    color: ${props => props.theme.linkHoverColor};

    text-decoration: underline;
  }
`

const HighlightWrapper = styled.div`
  display: flex;
`
const HighlightKey = styled.div`
  font-weight: 500;
  font-size: 13px;
  margin-right: 2.5px;
`
const HighlightValue = styled.div`
  font-size: 13px;
  max-width: 300px;
  .value {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`

const Svg = p => <svg width="20" height="20" viewBox="0 0 24 24" focusable="false" role="presentation" {...p} />

const DropdownIndicator = () => (
  <div css={{ height: 24, width: 28, opacity: 0.55 }}>
    <Svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Svg>
  </div>
)

const Option = props => {
  const { data } = props
  const { type, icon, label } = data

  return (
    <components.Option {...props}>
      <SidebarMenuIcon>
        <i className={`${icon}`} />
      </SidebarMenuIcon>
      <span>{label}</span>
    </components.Option>
  )
}

const parseValue = value => {
  let html = ''
  for (let i = 0; i < 5; i++) {
    if (value[i]) html += `${santizeElasticSearchTags(value[i])}\n`
  }

  return html
}

const santizeElasticSearchTags = value => {
  let parsedValue = value.replaceAll(OPENING_ES_TAG, REPLACEMENT_OPENING_ES_TAG)
  parsedValue = parsedValue.replaceAll(CLOSING_ES_TAG, REPLACEMENT_CLOSING_ES_TAG)

  return parsedValue
}

const SearchOption = props => {
  const { data } = props
  const { highlight, source } = data || {}

  return (
    <components.Option {...props}>
      <ResultLink>{generateDisplayLink(source)}</ResultLink>
      {Object.keys(highlight).map((key, index) => (
        <HighlightWrapper key={key}>
          <HighlightKey key={key}>{key}:</HighlightKey>
          <HighlightValue dangerouslySetInnerHTML={{ __html: parseValue(highlight[key]) }} />
        </HighlightWrapper>
      ))}
    </components.Option>
  )
}

const SelectStyles = props => ({
  control: provided => ({ ...provided, minWidth: 220, margin: 8, fontSize: '13.75px', transition: 'none' }),
  menu: provided => ({
    ...provided,
    position: 'relative;',
    fontSize: '13.75px',
    zIndex: 102,
    boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0px',
    marginBottom: '1px'
  }),
  option: (provided, state) => ({
    ...provided,
    outline: 'none'
  })
})
